import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import {
	AdminConnexZoneOption,
	AdminXpressConnexService,
	ConnexRouteForBookingsOptionDto,
	ConnexSlotOptionDto,
	ConnexWalkInCustomerLookupDto
} from 'app/services';
import { DeliveryBookingTypeEnum } from 'app/models';
import { Utils } from 'app/utils';
import { MessageService } from 'primeng/api';
import moment from 'moment';

interface WalkInBookingForm {
	route: ConnexRouteForBookingsOptionDto | null;
	pickupZone: AdminConnexZoneOption | null;
	dropoffZone: AdminConnexZoneOption | null;
	date: Date | null;
	slot: ConnexSlotOptionDto | null;
	guestName: string;
	guestPhone: string;
	xpressUserId: number | null;
	selectedCustomer: ConnexWalkInCustomerLookupDto | null;
	paxCount: number;
	vehicleCount: number;
	riderInstructions: string;
}

@Component({
	selector: 'connex-walk-in-booking-drawer',
	templateUrl: './connex-walk-in-booking-drawer.component.html',
	styleUrls: ['./connex-walk-in-booking-drawer.component.scss']
})
export class ConnexWalkInBookingDrawerComponent implements OnDestroy {
	private static readonly pesoWithCentavosFmt = new Intl.NumberFormat('en-PH', {
		maximumFractionDigits: 2,
		minimumFractionDigits: 2
	});
	private static readonly guestNamePattern = /^[\p{L}\p{M}'.\-\s]+$/u;

	@Input()
	public connexZoneOptions: AdminConnexZoneOption[] = [];

	@Output()
	public created = new EventEmitter<void>();

	public readonly deliveryBookingTypeEnum = DeliveryBookingTypeEnum;

	public readonly walkInDrawerPanelStyle: Record<string, string> = {
		width: '712px'
	};

	public walkInDrawerVisible = false;
	public walkInConfirmVisible = false;
	public walkInStep = 1;
	public walkInRouteOptions: ConnexRouteForBookingsOptionDto[] = [];
	public walkInPickupZoneOptions: AdminConnexZoneOption[] = [];
	public walkInDropoffZoneOptions: AdminConnexZoneOption[] = [];
	public walkInSlots: ConnexSlotOptionDto[] = [];
	public walkInLoadingSlots = false;
	public walkInLoadingRoutes = false;
	public walkInSubmitting = false;
	public walkInCashCollected = false;
	public walkInConfirmBookingType: DeliveryBookingTypeEnum = DeliveryBookingTypeEnum.WalkIn;
	public walkInMinDate = new Date();
	public walkInForm: WalkInBookingForm = this.createEmptyWalkInForm();
	public walkInCustomerMatches: ConnexWalkInCustomerLookupDto[] = [];
	public walkInCustomerAutocompleteValue: ConnexWalkInCustomerLookupDto | string | null = '+63';
	public walkInCustomerLookupLoading = false;
	public walkInGuestNameTouched = false;
	public walkInGuestPhoneTouched = false;

	private walkInSlotFilterDebounceTimer: number | undefined;
	private walkInLastLookupPhone = '';
	private walkInSuppressSlotFilterReload = false;
	private walkInRouteFiltersTouched = {
		pickup: false,
		dropoff: false,
		date: false
	};
	private walkInActiveRouteId: number | null = null;

	constructor(
		private adminConnexService: AdminXpressConnexService,
		private messageService: MessageService
	) {}

	ngOnDestroy(): void {
		if (this.walkInSlotFilterDebounceTimer) {
			window.clearTimeout(this.walkInSlotFilterDebounceTimer);
		}
	}

	open(): void {
		this.walkInForm = this.createEmptyWalkInForm();
		this.walkInSlots = [];
		this.walkInPickupZoneOptions = [];
		this.walkInDropoffZoneOptions = [];
		this.walkInActiveRouteId = null;
		this.walkInCustomerMatches = [];
		this.walkInCustomerAutocompleteValue = this.walkInForm.guestPhone;
		this.walkInCustomerLookupLoading = false;
		this.walkInGuestNameTouched = false;
		this.walkInGuestPhoneTouched = false;
		this.walkInLastLookupPhone = '';
		this.walkInMinDate = this.todayWalkInDate();
		this.walkInStep = 1;
		this.walkInCashCollected = false;
		this.walkInConfirmVisible = false;
		this.walkInDrawerVisible = true;
		void this.loadWalkInRoutes();
	}

	close(): void {
		if (this.walkInSubmitting) {
			return;
		}
		this.walkInDrawerVisible = false;
		this.walkInConfirmVisible = false;
	}

	onDrawerHide(): void {
		if (this.walkInSubmitting) {
			this.walkInDrawerVisible = true;
			return;
		}
		this.walkInConfirmVisible = false;
	}

	onWalkInRouteChange(): void {
		const routeId = this.walkInForm.route?.RouteId ?? null;
		if (routeId === this.walkInActiveRouteId) {
			return;
		}

		this.walkInActiveRouteId = routeId;
		this.walkInForm.pickupZone = null;
		this.walkInForm.dropoffZone = null;
		this.walkInForm.slot = null;
		this.walkInSlots = [];
		this.walkInPickupZoneOptions = [];
		this.walkInDropoffZoneOptions = [];
		this.walkInRouteFiltersTouched = { pickup: false, dropoff: false, date: false };

		if (!routeId) {
			this.walkInForm.date = null;
			return;
		}

		this.walkInForm.date = this.todayWalkInDate();
		this.walkInRouteFiltersTouched.date = true;
		void this.loadWalkInSlots();
	}

	onWalkInPickupZoneChange(): void {
		if (this.walkInSuppressSlotFilterReload) {
			return;
		}
		this.walkInRouteFiltersTouched.pickup = true;
		this.scheduleWalkInSlotFilterReload();
	}

	onWalkInDropoffZoneChange(): void {
		if (this.walkInSuppressSlotFilterReload) {
			return;
		}
		this.walkInRouteFiltersTouched.dropoff = true;
		this.scheduleWalkInSlotFilterReload();
	}

	onWalkInDateChange(): void {
		if (this.walkInSuppressSlotFilterReload) {
			return;
		}
		if (this.walkInForm.date) {
			this.walkInForm.date = Utils.normalizeDateOnly(this.walkInForm.date);
		}
		this.walkInRouteFiltersTouched.date = true;
		this.scheduleWalkInSlotFilterReload();
	}

	onWalkInSlotSelect(slot: ConnexSlotOptionDto): void {
		this.walkInSuppressSlotFilterReload = true;
		this.walkInForm.slot = slot;
		this.walkInForm.pickupZone = this.findConnexZoneOption(slot.PickupZoneId, slot.PickupZoneName);
		this.walkInForm.dropoffZone = this.findConnexZoneOption(slot.DropoffZoneId, slot.DropoffZoneName);
		this.walkInForm.date = Utils.dateOnlyFromIso(
			moment.parseZone(slot.DepartureAt).format('YYYY-MM-DD')
		);
		this.walkInRouteFiltersTouched = { pickup: true, dropoff: true, date: true };
		window.setTimeout(() => {
			this.walkInSuppressSlotFilterReload = false;
		});
	}

	isWalkInSlotSelected(slot: ConnexSlotOptionDto): boolean {
		return !!this.walkInForm.slot
			&& this.walkInForm.slot.SlotId === slot.SlotId
			&& this.walkInForm.slot.DepartureAt === slot.DepartureAt;
	}

	adjustWalkInPax(delta: number): void {
		const max = this.walkInSelectedSlot?.MaxPassengersPerVehicle ?? 1;
		const next = Math.max(1, Math.min(max, this.walkInForm.paxCount + delta));
		this.walkInForm.paxCount = next;
	}

	continueWalkIn(): void {
		if (this.walkInStep === 1) {
			if (!this.walkInCanContinueRoute) {
				this.messageService.add({
					severity: 'warn',
					summary: 'Incomplete booking',
					detail: 'Please select route, pickup, drop-off, date, and slot.'
				});
				return;
			}
			this.walkInStep = 2;
			return;
		}

		if (this.walkInStep === 2) {
			this.walkInGuestNameTouched = true;
			this.walkInGuestPhoneTouched = true;
			if (!this.walkInCanContinuePassenger) {
				this.messageService.add({
					severity: 'warn',
					summary: 'Incomplete booking',
					detail: this.walkInPassengerValidationMessage
				});
				return;
			}
			this.walkInStep = 3;
		}
	}

	onWalkInGuestPhoneChange(): void {
		const normalizedPhone = this.normalizeGuestPhone(this.walkInForm.guestPhone);
		if (this.walkInForm.selectedCustomer
			&& normalizedPhone !== this.normalizeGuestPhone(this.walkInForm.selectedCustomer.Phone)) {
			this.clearWalkInCustomerSelection(false);
			return;
		}

		if (!this.walkInForm.selectedCustomer) {
			this.walkInForm.xpressUserId = null;
		}
	}

	onWalkInGuestPhoneBlur(): void {
		this.walkInGuestPhoneTouched = true;
	}

	onWalkInCustomerSelected(customer: ConnexWalkInCustomerLookupDto | null): void {
		if (!customer) {
			this.clearWalkInCustomerSelection(false);
			return;
		}

		this.walkInForm.selectedCustomer = customer;
		this.walkInForm.xpressUserId = customer.XpressUserId;
		this.walkInForm.guestPhone = customer.Phone;
		this.walkInCustomerAutocompleteValue = customer;
	}

	onWalkInCustomerAutocompleteChange(value: ConnexWalkInCustomerLookupDto | string | null): void {
		this.walkInCustomerAutocompleteValue = value;
		if (typeof value === 'string') {
			this.walkInForm.guestPhone = value;
			this.onWalkInGuestPhoneChange();
			return;
		}

		if (!value) {
			this.walkInForm.guestPhone = '';
			this.clearWalkInCustomerSelection(true);
		}
	}

	onWalkInCustomerAutocompleteSelect(event: { value?: ConnexWalkInCustomerLookupDto }): void {
		this.onWalkInCustomerSelected(event.value ?? null);
	}

	onWalkInCustomerAutocompleteClear(): void {
		this.walkInForm.guestPhone = '';
		this.clearWalkInCustomerSelection(true);
	}

	async searchWalkInCustomersByPhone(event: { query?: string }): Promise<void> {
		this.walkInForm.guestPhone = event.query ?? '';
		await this.lookupWalkInCustomerByPhone();
	}

	backWalkIn(): void {
		this.walkInStep = Math.max(1, this.walkInStep - 1);
	}

	openWalkInConfirm(bookingType: DeliveryBookingTypeEnum): void {
		if (!this.walkInCanSubmit) {
			return;
		}
		this.walkInConfirmBookingType = bookingType;
		this.walkInCashCollected = false;
		this.walkInConfirmVisible = true;
	}

	cancelWalkInConfirm(): void {
		if (this.walkInSubmitting) {
			return;
		}
		this.walkInConfirmVisible = false;
	}

	async confirmWalkInBooking(): Promise<void> {
		if (!this.walkInCashCollected) {
			return;
		}
		await this.placeWalkInBooking(this.walkInConfirmBookingType);
	}

	get walkInSelectedSlot(): ConnexSlotOptionDto | null {
		return this.walkInForm.slot;
	}

	get walkInShowRouteFilters(): boolean {
		return !!this.walkInForm.route;
	}

	get walkInCanContinueRoute(): boolean {
		return !!this.walkInForm.route
			&& !!this.walkInForm.pickupZone
			&& !!this.walkInForm.dropoffZone
			&& !!this.walkInForm.date
			&& !!this.walkInForm.slot;
	}

	get walkInCanContinuePassenger(): boolean {
		return this.isValidGuestName(this.walkInForm.guestName)
			&& this.isValidGuestPhone(this.walkInForm.guestPhone)
			&& this.walkInForm.paxCount >= 1
			&& this.walkInForm.paxCount <= (this.walkInSelectedSlot?.MaxPassengersPerVehicle ?? 1);
	}

	get walkInGuestNameError(): string | null {
		if (!this.walkInGuestNameTouched) {
			return null;
		}

		const name = this.walkInForm.guestName.trim();
		if (!name) {
			return 'Customer name is required';
		}

		if (name.length < 2 || name.length > 80) {
			return 'Customer name must be between 2 and 80 characters';
		}

		if (!ConnexWalkInBookingDrawerComponent.guestNamePattern.test(name)) {
			return 'Customer name contains invalid characters';
		}

		return null;
	}

	get walkInGuestPhoneError(): string | null {
		if (!this.walkInGuestPhoneTouched) {
			return null;
		}

		const phone = this.normalizeGuestPhone(this.walkInForm.guestPhone);
		if (!phone) {
			return 'Mobile number is required';
		}

		if (!this.isValidGuestPhone(phone)) {
			return 'Enter a valid mobile number, 10-14 digits with optional + prefix';
		}

		return null;
	}

	get walkInPassengerValidationMessage(): string {
		if (this.walkInGuestNameError) {
			return this.walkInGuestNameError;
		}

		if (this.walkInGuestPhoneError) {
			return this.walkInGuestPhoneError;
		}

		return 'Please fill customer name, mobile number, and passenger count.';
	}

	get walkInPassengerTypeLabel(): string {
		return this.walkInForm.xpressUserId ? 'Existing customer' : 'First-time';
	}

	get walkInCanSubmit(): boolean {
		return this.walkInStep === 3 && this.walkInCanContinueRoute && this.walkInCanContinuePassenger;
	}

	get walkInCanBookScheduled(): boolean {
		return this.walkInCanSubmit;
	}

	get isWalkInImmediateDispatchConfirm(): boolean {
		return this.walkInConfirmBookingType === DeliveryBookingTypeEnum.WalkInImmediateDispatch;
	}

	get walkInConfirmTitle(): string {
		return this.isWalkInImmediateDispatchConfirm
			? 'Confirm immediate dispatch?'
			: 'Confirm walk-in booking?';
	}

	get walkInConfirmBody(): string {
		return this.isWalkInImmediateDispatchConfirm
			? 'This will assign the nearest available driver right away and add the walk-in premium to the fare.'
			: 'This will create a scheduled ConneX booking and deduct the selected slot inventory.';
	}

	get walkInConfirmIconClass(): string {
		return this.isWalkInImmediateDispatchConfirm
			? 'pi-exclamation-triangle is-warning'
			: 'pi-check-circle is-success';
	}

	get walkInConfirmBaseFareLabel(): string {
		return `${this.isWalkInImmediateDispatchConfirm ? 'Base fare' : 'Slot fare'} x ${this.walkInForm.vehicleCount}`;
	}

	get walkInConfirmTotalLabel(): string {
		return this.isWalkInImmediateDispatchConfirm
			? this.walkInTotalFareLabel
			: this.walkInBookTotalLabel;
	}

	get walkInDepartureDateForApi(): string {
		if (!this.walkInSelectedSlot) {
			return '';
		}
		return moment.parseZone(this.walkInSelectedSlot.DepartureAt).format('YYYY-MM-DD');
	}

	get walkInTripRouteLabel(): string {
		return this.walkInForm.route?.Name ?? '—';
	}

	get walkInPickupLabel(): string {
		return this.walkInForm.pickupZone?.Name || this.walkInForm.pickupZone?.Address || this.walkInSelectedSlot?.PickupZoneName || '—';
	}

	get walkInDropoffLabel(): string {
		return this.walkInForm.dropoffZone?.Name || this.walkInForm.dropoffZone?.Address || this.walkInSelectedSlot?.DropoffZoneName || '—';
	}

	get walkInScheduleLabel(): string {
		if (!this.walkInSelectedSlot) {
			return '—';
		}
		return moment.parseZone(this.walkInSelectedSlot.DepartureAt).format('MMM D, YYYY · h:mm A');
	}

	get walkInPassengerInitials(): string {
		const parts = this.walkInForm.guestName.trim().split(/\s+/).filter(Boolean);
		if (parts.length === 0) {
			return 'NA';
		}
		return parts.slice(0, 2).map(part => part[0]?.toUpperCase()).join('');
	}

	get walkInBaseFareLabel(): string {
		return this.walkInMoney(this.walkInBaseFareAmount);
	}

	get walkInPremiumLabel(): string {
		return this.walkInMoney(this.walkInSelectedSlot?.PremiumFareAmount);
	}

	get walkInTotalFareLabel(): string {
		return this.walkInMoney(this.walkInTotalFareAmount);
	}

	get walkInBookTotalLabel(): string {
		return this.walkInMoney(this.walkInBaseFareAmount);
	}

	get walkInBaseFareAmount(): number {
		return (this.walkInSelectedSlot?.TotalAmount ?? 0) * this.walkInForm.vehicleCount;
	}

	get walkInTotalFareAmount(): number {
		return this.walkInSelectedSlot?.WalkInImmediateDispatchAmount ?? 0;
	}

	walkInExpectedAmountForApi(bookingType: DeliveryBookingTypeEnum): number {
		return bookingType === DeliveryBookingTypeEnum.WalkInImmediateDispatch
			? (this.walkInSelectedSlot?.WalkInImmediateDispatchAmount ?? 0)
			: (this.walkInSelectedSlot?.TotalAmount ?? 0);
	}

	slotOptionTime(slot: ConnexSlotOptionDto | null | undefined): string {
		if (!slot) {
			return '';
		}
		return moment.parseZone(slot.DepartureAt).format('h:mm A');
	}

	slotOptionFare(slot: ConnexSlotOptionDto | null | undefined): string {
		if (!slot) {
			return '';
		}
		return this.walkInMoney(slot.TotalAmount);
	}

	walkInSlotStatusLabel(slot: ConnexSlotOptionDto): string {
		if (!slot.IsAvailable) {
			return 'Full';
		}

		return slot.VehicleCapacity === 1 ? '1 Left' : `${slot.VehicleCapacity} Open`;
	}

	walkInSlotStatusClass(slot: ConnexSlotOptionDto): string {
		if (!slot.IsAvailable) {
			return 'is-full';
		}

		return slot.VehicleCapacity === 1 ? 'is-warning' : 'is-available';
	}

	private async placeWalkInBooking(bookingType: DeliveryBookingTypeEnum): Promise<void> {
		if (!this.walkInSelectedSlot || !this.walkInForm.route || !this.walkInForm.pickupZone || !this.walkInForm.dropoffZone) {
			return;
		}

		const isImmediateDispatch = bookingType === DeliveryBookingTypeEnum.WalkInImmediateDispatch;
		if (!this.walkInCashCollected) {
			return;
		}

		this.walkInSubmitting = true;
		try {
			const dto = {
				BookingType: bookingType,
				SlotId: this.walkInSelectedSlot.SlotId,
				DepartureDate: this.walkInDepartureDateForApi,
				ExpectedAmount: this.walkInExpectedAmountForApi(bookingType),
				PickupZoneId: this.walkInForm.pickupZone.Id,
				DropoffZoneId: this.walkInForm.dropoffZone.Id,
				PaxCount: this.walkInForm.paxCount,
				XpressUserId: this.walkInForm.xpressUserId,
				GuestName: this.walkInForm.guestName.trim(),
				GuestPhone: this.normalizeGuestPhone(this.walkInForm.guestPhone),
				RiderInstructions: this.walkInForm.riderInstructions.trim(),
				IsCashCollected: this.walkInCashCollected
			};

			const response = await this.adminConnexService.placeConnexWalkInDeliveryOrder(
				dto,
				this.messageService
			);

			if (!response?.Success) {
				this.messageService.add({
					severity: 'error',
					summary: 'Unable to Create Booking',
					detail: response?.ErrorMessage ?? (isImmediateDispatch
						? 'No available driver found for immediate dispatch'
						: 'Unexpected error occurred')
				});
				return;
			}

			this.messageService.add({
				severity: 'success',
				summary: isImmediateDispatch ? 'Immediate Dispatch Created' : 'Walk-in Booking Created',
				detail: isImmediateDispatch
					? (response.Id
						? `Booking #${response.Id} has been assigned for immediate dispatch.`
						: 'Booking has been assigned for immediate dispatch.')
					: (response.Id ? `Booking #${response.Id} has been created.` : 'Booking has been created.')
			});
			this.walkInConfirmVisible = false;
			this.walkInDrawerVisible = false;
			this.created.emit();
		} finally {
			this.walkInSubmitting = false;
		}
	}

	private createEmptyWalkInForm(): WalkInBookingForm {
		return {
			route: null,
			pickupZone: null,
			dropoffZone: null,
			date: null,
			slot: null,
			guestName: '',
			guestPhone: '+63',
			xpressUserId: null,
			selectedCustomer: null,
			paxCount: 1,
			vehicleCount: 1,
			riderInstructions: ''
		};
	}

	private async lookupWalkInCustomerByPhone(): Promise<void> {
		const normalizedPhone = this.normalizeGuestPhone(this.walkInForm.guestPhone);
		if (!this.isValidGuestPhone(normalizedPhone)) {
			this.clearWalkInCustomerSelection(true);
			return;
		}

		if (normalizedPhone === this.walkInLastLookupPhone) {
			return;
		}

		this.walkInCustomerLookupLoading = true;
		try {
			const response = await this.adminConnexService.lookupConnexWalkInCustomer(
				normalizedPhone,
				this.messageService
			);

			if (!response?.Success) {
				this.walkInCustomerMatches = [];
				this.walkInLastLookupPhone = '';
				return;
			}

			this.walkInLastLookupPhone = normalizedPhone;
			this.walkInCustomerMatches = response.Customers ?? [];

			if (this.walkInForm.selectedCustomer
				&& !this.walkInCustomerMatches.some(customer =>
					customer.XpressUserId === this.walkInForm.selectedCustomer?.XpressUserId)) {
				this.clearWalkInCustomerSelection(false);
			} else if (this.walkInForm.selectedCustomer) {
				this.walkInForm.xpressUserId = this.walkInForm.selectedCustomer.XpressUserId;
			} else {
				this.walkInForm.xpressUserId = this.walkInCustomerMatches[0]?.XpressUserId ?? null;
			}
		} finally {
			this.walkInCustomerLookupLoading = false;
		}
	}

	private clearWalkInCustomerSelection(clearMatches: boolean): void {
		this.walkInForm.selectedCustomer = null;
		this.walkInForm.xpressUserId = null;
		if (clearMatches) {
			this.walkInCustomerMatches = [];
			this.walkInLastLookupPhone = '';
		}
	}

	private normalizeGuestPhone(phone: string): string {
		return phone.replace(/\s/g, '').replace(/-/g, '').trim();
	}

	private isValidGuestName(name: string): boolean {
		const trimmed = name.trim();
		return trimmed.length >= 2
			&& trimmed.length <= 80
			&& ConnexWalkInBookingDrawerComponent.guestNamePattern.test(trimmed);
	}

	private isValidGuestPhone(phone: string): boolean {
		return Utils.mobileNumberRegex.test(this.normalizeGuestPhone(phone));
	}

	private async loadWalkInRoutes(): Promise<void> {
		this.walkInLoadingRoutes = true;
		try {
			const response = await this.adminConnexService.getConnexRoutesForBookings(this.messageService);
			if (!response?.Success) {
				this.messageService.add({
					severity: 'error',
					summary: 'Unable to Load Routes',
					detail: response?.ErrorMessage ?? 'Unexpected error occurred'
				});
				return;
			}
			this.walkInRouteOptions = response.Routes ?? [];
		} finally {
			this.walkInLoadingRoutes = false;
		}
	}

	private scheduleWalkInSlotFilterReload(): void {
		this.walkInForm.slot = null;
		if (this.walkInSlotFilterDebounceTimer) {
			window.clearTimeout(this.walkInSlotFilterDebounceTimer);
		}
		this.walkInSlotFilterDebounceTimer = window.setTimeout(() => {
			this.walkInSlotFilterDebounceTimer = undefined;
			void this.loadWalkInSlots();
		}, 300);
	}

	private async loadWalkInSlots(): Promise<void> {
		if (!this.walkInForm.route) {
			return;
		}

		this.walkInLoadingSlots = true;
		try {
			const request: {
				RouteId: number;
				PickupZoneId?: number;
				DropoffZoneId?: number;
				DepartureDate?: string;
				EnforceBookingWindow: boolean;
			} = {
				RouteId: this.walkInForm.route.RouteId,
				EnforceBookingWindow: false
			};

			if (this.walkInRouteFiltersTouched.pickup && this.walkInForm.pickupZone?.Id) {
				request.PickupZoneId = this.walkInForm.pickupZone.Id;
			}
			if (this.walkInRouteFiltersTouched.dropoff && this.walkInForm.dropoffZone?.Id) {
				request.DropoffZoneId = this.walkInForm.dropoffZone.Id;
			}
			if (this.walkInForm.date) {
				request.DepartureDate = Utils.formatPHCalendarDate(this.walkInForm.date);
			}

			const response = await this.adminConnexService.getConnexSlotsForBookings(
				request,
				this.messageService
			);

			if (!response?.Success) {
				this.messageService.add({
					severity: 'error',
					summary: 'Unable to Load Slots',
					detail: response?.ErrorMessage ?? 'Unexpected error occurred'
				});
				return;
			}

			this.walkInSlots = response.Slots ?? [];
			this.updateWalkInZoneOptionsFromSlots();

			const selected = this.walkInForm.slot;
			if (selected && !this.walkInSlots.some(slot =>
				slot.SlotId === selected.SlotId && slot.DepartureAt === selected.DepartureAt)) {
				this.walkInForm.slot = null;
			}
		} finally {
			this.walkInLoadingSlots = false;
		}
	}

	private updateWalkInZoneOptionsFromSlots(): void {
		const pickupIds = new Set(this.walkInSlots.map(slot => slot.PickupZoneId));
		const dropoffIds = new Set(this.walkInSlots.map(slot => slot.DropoffZoneId));

		this.walkInPickupZoneOptions = this.connexZoneOptions.filter(zone => pickupIds.has(zone.Id));
		this.walkInDropoffZoneOptions = this.connexZoneOptions.filter(zone => dropoffIds.has(zone.Id));
	}

	private findConnexZoneOption(zoneId: number, zoneName: string): AdminConnexZoneOption | null {
		return this.connexZoneOptions.find(zone => zone.Id === zoneId) ?? {
			Id: zoneId,
			Name: zoneName,
			Address: zoneName
		};
	}

	private todayWalkInDate(): Date {
		return Utils.getPHTodayCalendarDate();
	}

	private walkInMoney(amount: number | undefined | null): string {
		return `₱${ConnexWalkInBookingDrawerComponent.pesoWithCentavosFmt.format(amount ?? 0)}`;
	}
}
